// O LEITOR ABRIU E NÃO HÁ PÁGINA PARA DESENHAR — `T10.2`.
//
// ── O ACHADO, NA LETRA DE QUEM TESTOU (25/09/2026) ──────────────────────────────────────────
//
//     "Quando eu clico no meu quadrinho, ele não acha o quadrinho: mostra só uma página em
//      branco. Quando clico no card de baixo, não aparece quadrinho, só uma página em branco."
//
//     "Se eu vou na biblioteca e clico no meu quadrinho, não funciona: na biblioteca eu não
//      consigo olhar o quadrinho."
//
// ── A CAUSA, E ELA ESTAVA NOS DOIS LEITORES, IGUAL ──────────────────────────────────────────
//
// Os dois leitores deste produto desenham as páginas com um `map` sobre a lista, e **nenhum dos
// dois perguntava se a lista está vazia**:
//
//   `biblioteca/ler-historia.tsx`     -> `VisualizadorDeDocumento` com `paginas: []` devolve uma
//                                        `<div class="motor-documento">` VAZIA. Nada dentro.
//   `meus-quadrinhos/ver-quadrinho.tsx` -> `paginas.map(...)` sobre lista vazia, dentro de
//                                        `.vq-folhas`. Nada dentro.
//
// Em `ler-historia.tsx` havia quatro telas escritas com cuidado para quatro mundos — abrindo, não
// achei, não deu, sem acesso — e o quinto mundo não tinha tela nenhuma: **a leitura deu certo e
// não veio página**. O estado virava `achei`, e `achei` desenha o leitor. Um leitor sem página é
// uma área em branco, sem frase e sem saída, exatamente embaixo de um cabeçalho que diz o título
// e o autor. É o que ele descreve.
//
// ── O TERCEIRO MUNDO É O QUE IMPORTA, E ELE NÃO PODE SER CONTADO COMO OS OUTROS ─────────────
//
// Há três situações diferentes, e tratá-las com uma frase só seria repetir o defeito do
// `T8.23` — um título que afirma sobre a prateleira em cima de um parágrafo que afirma sobre o
// filtro:
//
//   1. a história não tem página nenhuma e a ficha concorda. Está incompleta, e dizer isso basta;
//   2. **a ficha diz que tem N páginas e nenhuma chegou.** Aqui a tela não pode dizer "está
//      incompleta", porque não está: o que falhou foi buscar o que existe. Quem lê precisa da
//      ação que funciona, que é tentar de novo — e do número, que é o que impede a frase de
//      mentir;
//   3. há página. Desenhe, e esta função sai do caminho devolvendo `null`.
//
// ── POR QUE É FUNÇÃO PURA, E NÃO UM `if` DENTRO DO JSX ──────────────────────────────────────
//
// Pelo mesmo motivo do `estado-vazio.ts` da biblioteca: o defeito não estava em renderizar, e
// estava em NÃO TER DECIDIDO o que a tela diz. E porque são DOIS leitores — um `if` em cada um é
// a garantia de que o segundo divirja do primeiro na próxima mudança. O teste pina a frase sem
// abrir navegador.

/** O mundo, no instante em que o leitor vai desenhar. */
export interface MundoDoLeitor {
  /** Quantas páginas chegaram para desenhar. */
  quantasChegaram: number;
  /**
   * Quantas páginas o registro do quadrinho DIZ que ele tem.
   *
   * Pode discordar do que chegou, e é essa discordância que separa "incompleto" de "não
   * carregou". Quando não há registro nenhum, passe `0`.
   */
  quantasORegistroDiz: number;
  /** `biblioteca` é a história publicada de alguém; `acervo` é o quadrinho de quem está lendo. */
  onde: "biblioteca" | "acervo";
}

export interface AvisoDoLeitor {
  titulo: string;
  explicacao: string;
  /** A saída oferecida, ou `null` quando nenhuma é honesta. */
  acao: { rotulo: string; destino: string } | null;
  /** `true` quando a saída é RECARREGAR, e não navegar para outro endereço. */
  tentarDeNovo: boolean;
}

const plural = (n: number) => (n === 1 ? "1 página" : `${n} páginas`);

/**
 * O que o leitor mostra no lugar das páginas — ou `null` quando há página e ele deve desenhar.
 */
export function quandoNaoHaPagina(m: MundoDoLeitor): AvisoDoLeitor | null {
  if (m.quantasChegaram > 0) return null;

  // O MUNDO 2: o registro afirma que existe, e não veio. Nunca dizer "incompleto" aqui — seria a
  // tela mentindo sobre o trabalho de uma criança.
  if (m.quantasORegistroDiz > 0) {
    return {
      titulo: "As páginas não chegaram agora",
      explicacao:
        `Este quadrinho tem ${plural(m.quantasORegistroDiz)}, e nenhuma delas carregou. ` +
        "O trabalho está guardado — o que falhou foi buscar as páginas.",
      acao: { rotulo: "Tentar de novo", destino: "" },
      tentarDeNovo: true,
    };
  }

  // O MUNDO 1, e a frase muda com o dono: no acervo a saída é o editor, porque quem está lendo é
  // quem pode consertar. Na biblioteca não existe saída honesta para dentro da história — quem
  // publicou é outra pessoa —, então a saída é voltar à prateleira.
  if (m.onde === "acervo") {
    return {
      titulo: "Este quadrinho ainda não tem página",
      explicacao:
        "Ele foi criado e nenhuma página foi montada ainda. Abra o editor para fazer a primeira.",
      acao: { rotulo: "Editar este quadrinho", destino: "editar" },
      tentarDeNovo: false,
    };
  }

  return {
    titulo: "Esta história foi publicada sem página",
    explicacao:
      "A ficha dela está aqui, com título e autor, e nenhuma página veio junto. Quem publicou " +
      "pode completá-la publicando de novo.",
    acao: { rotulo: "Voltar para a biblioteca", destino: "/biblioteca" },
    tentarDeNovo: false,
  };
}
